"use client";

import { useEffect, useState } from "react";
import { Moon, Sun } from "lucide-react";
import { cn } from "@/lib/utils";

export default function ThemeToggle({ className }) {
  const [dark, setDark] = useState(false);

  useEffect(() => {
    setDark(document.documentElement.classList.contains("dark"));
  }, []);

  function toggle(event) {
    const next = !dark;
    const apply = () => {
      const root = document.documentElement;
      root.classList.add("theme-switching");
      root.classList.toggle("dark", next);
      root.getBoundingClientRect(); // flush styles while transitions are off, so colours flip in one frame
      root.classList.remove("theme-switching");
      try {
        localStorage.setItem("m73-theme", next ? "dark" : "light");
      } catch {}
      setDark(next);
    };

    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (!document.startViewTransition || reduce) return apply();

    // Reveal from the button's centre: keyboard and programmatic clicks report clientX/Y as 0.
    const box = event.currentTarget.getBoundingClientRect();
    const x = box.left + box.width / 2;
    const y = box.top + box.height / 2;
    const r = Math.hypot(Math.max(x, innerWidth - x), Math.max(y, innerHeight - y));
    const transition = document.startViewTransition(apply);
    transition.ready.then(
      () => {
        // Even easing: a strong ease-out left the far edges creeping in for most of the duration.
        document.documentElement.animate(
          { clipPath: [`circle(0px at ${x}px ${y}px)`, `circle(${r}px at ${x}px ${y}px)`] },
          { duration: 600, easing: "ease-in-out", pseudoElement: "::view-transition-new(root)" }
        );
      },
      () => {} // skipped (e.g. a second quick click): nothing to animate
    );
  }

  return (
    <button
      type="button"
      onClick={toggle}
      aria-label={dark ? "Switch to light theme" : "Switch to dark theme"}
      className={cn(
        "relative flex size-10 items-center justify-center overflow-hidden rounded-full border border-border bg-background text-foreground transition-colors hover:bg-accent",
        className
      )}
    >
      <Sun className={cn("absolute size-[18px] transition-all duration-500", dark ? "rotate-90 scale-0 opacity-0" : "rotate-0 scale-100 opacity-100")} aria-hidden="true" />
      <Moon className={cn("absolute size-[18px] transition-all duration-500", dark ? "rotate-0 scale-100 opacity-100" : "-rotate-90 scale-0 opacity-0")} aria-hidden="true" />
    </button>
  );
}
